import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import { ALL_CATEGORY_LABELS } from './unbilledCharges/categories';
/**
 * Unbilled Charges — review grid + summary report.
 *
 * Rows are PRO x accessorial category pairs where accrued cost exceeds billed
 * revenue (nightly cms.unbilled_charges_cache rebuild; the backend recomputes
 * revenue live so a row disappears as soon as the charge is billed).
 *
 * Three annotation columns are editable, staged locally and bulk-saved
 * (LcResolutionBoard's pattern), with field-level component permissions:
 *   - LC Reason           -> 'unbilled-lc-reason'   (GWI360-LogisticCoordinators)
 *   - Responsible Party + Comment -> 'unbilled-mgmt-fields' (GWI360-LCManager)
 * Everyone with page access sees the values; the inputs disable without a
 * component grant (defaultDisabled). The PATCH endpoint re-checks server-side.
 */
import { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowTopRightOnSquareIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { formatIsoDateOnly } from '../../utils/dateUtils';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import type { BulkEditMeta } from '../../components/TanTable';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import SingleSelect, { SingleSelectOption } from '../../components/SingleSelect';
import FilterBar from '../../components/FilterBar';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import PermissionsContainer, { useResolvedAccessLevel } from '../../components/PermissionsContainer';
import { useTryPermissionsMode } from '../../contexts/PermissionsModeContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { useOrderDispatcherOptions } from '../../hooks/useDispatcherOptions';
import MatrixTable, { MatrixDatum, MatrixCellRef } from './unbilledCharges/MatrixTable';
import DrilldownModal from './unbilledCharges/DrilldownModal';
import type { UnbilledChargeRow, EditableField, MatrixAxis, DateBasis } from './unbilledCharges/types';
import Toggle from '../../components/Toggle';
import userSettingsService from '../../services/userSettings.service';
import { useToast } from '../../contexts/ToastContext';
import ordersService from '../../services/orders.service';
import api from '../../services/api';
import { config } from '../../config/config';

type RowEdits = Record<string, Partial<Record<EditableField, string>>>;

const LC_COMPONENT = 'unbilled-lc-reason';
const MGMT_COMPONENT = 'unbilled-mgmt-fields';

/**
 * Manual resolution for rows the live-revenue match can never link: the
 * customer required the charge billed under a different line-item label, so
 * no revenue lands on the category's litypes. Selecting it codes the row
 * (drops from the open queue) and excludes it from the Summary pivot — it's
 * billed, not a loss. Page-local on purpose — it must not join the V1
 * department taxonomy that /cms-lookups/departments mirrors (Additional Cost
 * lines share that list).
 */
const BILLED_OPTION = 'BILLED';

const MATRIX_AXIS_LABELS: Record<MatrixAxis, string> = {
  region: 'Region',
  lc: 'LC',
  dispatcher: 'Dispatcher',
};

/**
 * Which date the window applies to. "Delivered" = the order's delivery date;
 * "Charged" = the cost lines' own dates — a row is in the window when ANY of
 * its lines was incurred in it, which is the exposure view: what did we start
 * owing this month. Those dates are built by Sync, not here: the driver-pay
 * event date; for per diem and ssl chassis the date AP keyed the vendor
 * invoice number (falling back to the cost record's create date); for storage
 * always the create date, since storage is counted before any invoice exists.
 *
 * The basis only chooses WHICH rows appear. Both show a row's whole cost and
 * whole billed revenue, so a charge spanning two months is counted in full in
 * each of them and the months do NOT sum to the total. That is deliberate
 * (Lisa, 2026-10-27): billed revenue comes off the invoice and is not a
 * function of when the cost landed.
 */
const DATE_BASIS_LABELS: Record<DateBasis, string> = {
  delivery: 'Delivered',
  charge: 'Charged',
};
const BASIS_STORAGE_KEY = 'unbilledChargesDateBasis';

const readStoredBasis = (): DateBasis => {
  try {
    const v = localStorage.getItem(BASIS_STORAGE_KEY);
    return v === 'charge' ? 'charge' : 'delivery';
  } catch {
    return 'delivery';
  }
};

/** Blank-responsible label; the backend maps it back to '' in the
 * `responsible` filter, so keep the two in step (NOT_CODED server-side). */
const NOT_CODED_LABEL = '(Not coded)';

const DATE_PRESETS = ['today', 'mtd', 'lastMonth', 'ytd'] as const;
type DatePreset = (typeof DATE_PRESETS)[number];
const PRESET_STORAGE_KEY = 'unbilledChargesDatePreset';

/** Presets resolve against *today* every visit — a remembered MTD keeps
 * extending as the month advances, and a remembered Today follows the clock. */
const resolvePreset = (preset: DatePreset): { start: Date; end: Date } => {
  const now = new Date();
  if (preset === 'today') {
    return { start: now, end: now };
  }
  if (preset === 'mtd') {
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
  }
  if (preset === 'lastMonth') {
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      end: new Date(now.getFullYear(), now.getMonth(), 0),
    };
  }
  return { start: new Date(now.getFullYear(), 0, 1), end: now };
};

const readStoredPreset = (): DatePreset | null => {
  try {
    const v = localStorage.getItem(PRESET_STORAGE_KEY);
    return DATE_PRESETS.includes(v as DatePreset) ? (v as DatePreset) : null;
  } catch {
    return null;
  }
};

const rowKey = (row: Pick<UnbilledChargeRow, 'pronumber' | 'category'>): string =>
  `${row.pronumber}|${row.category}`;

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value: number | null): string => {
  if (value == null) return '-';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const sumBy = (
  rows: Array<{ original: UnbilledChargeRow }>,
  key: 'cost' | 'revenue' | 'margin'
): number => rows.reduce((acc, r) => acc + (r.original[key] || 0), 0);

/** Axios error detail — string for HTTPExceptions, {msg}[] for Pydantic. */
const errorMessage = (reason: any): string => {
  const detail = reason?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg || 'invalid').join('; ') || 'validation error';
  }
  return reason?.message || 'save failed';
};

/**
 * Truncated inline preview of the PRO's V1 rate comments (billability notes
 * live there as free text — what's billable at what price); newlines collapse
 * so the preview shows as much as fits the column. The full text pops over
 * instantly on hover AND toggles open/closed on click (click again,
 * click-outside, or Esc to close) — OrderFormView's PregateErrorIndicator
 * pattern, portaled to <body> and repositioned on scroll so it escapes the
 * grid's overflow clipping. Hover-close is delayed a beat so the pointer can
 * cross into the popover to scroll long comments without pinning.
 *
 * Pin state is owned by the page (one pinned popover across the grid): while
 * a row is pinned, other rows suppress their hover previews so mousing around
 * doesn't stack popovers over the one being read; clicking another row moves
 * the pin there.
 */
const RateCommentsCell: React.FC<{
  comments: string;
  pronumber: string;
  pinned: boolean;
  hoverSuppressed: boolean;
  onPinnedChange: (pinned: boolean) => void;
}> = ({ comments, pronumber, pinned, hoverSuppressed, onPinnedChange }) => {
  const [hovering, setHovering] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const hoverOffTimer = useRef<number | null>(null);
  const open = pinned || (hovering && !hoverSuppressed);

  const hoverIn = useCallback(() => {
    if (hoverOffTimer.current) {
      window.clearTimeout(hoverOffTimer.current);
      hoverOffTimer.current = null;
    }
    setHovering(true);
  }, []);
  const hoverOut = useCallback(() => {
    hoverOffTimer.current = window.setTimeout(() => setHovering(false), 120);
  }, []);
  useEffect(() => () => {
    if (hoverOffTimer.current) window.clearTimeout(hoverOffTimer.current);
  }, []);

  const computePos = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const pw = popRef.current?.offsetWidth ?? 384;
    const ph = popRef.current?.offsetHeight ?? 80;
    const pad = 8;
    let top = r.bottom + 6;
    let left = r.left;
    if (left + pw > window.innerWidth - pad) left = window.innerWidth - pw - pad;
    if (left < pad) left = pad;
    // Flip above the icon if it would overflow the bottom edge.
    if (top + ph > window.innerHeight - pad) top = r.top - ph - 6;
    if (top < pad) top = pad;
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    computePos();
    window.addEventListener('scroll', computePos, true);
    window.addEventListener('resize', computePos);
    return () => {
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [open, comments, computePos]);

  // Click-outside / Escape close the pinned popover.
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !popRef.current?.contains(t)) {
        onPinnedChange(false);
        setHovering(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onPinnedChange(false); setHovering(false); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned, onPinnedChange]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPinnedChange(!pinned);
          // Turning off while the pointer is still over the cell: clear the
          // hover flag too so it closes now instead of staying up via hover.
          if (pinned) setHovering(false);
        }}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        className={`block w-full truncate text-left text-xs cursor-pointer transition-colors ${
          pinned
            ? 'text-blue-700 dark:text-blue-300'
            : 'text-gray-700 hover:text-blue-700 dark:text-gray-200 dark:hover:text-blue-300'
        }`}
        aria-label={`Rate comments for PRO ${pronumber}`}
        aria-expanded={open}
      >
        {comments}
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          role="tooltip"
          onMouseEnter={hoverIn}
          onMouseLeave={hoverOut}
          className="fixed z-[9999] w-max max-w-md rounded-md border border-rule bg-panel shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="px-3 pt-2 text-[0.625rem] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Rate Comments
          </div>
          <div className="max-h-80 overflow-y-auto px-3 pb-2 pt-1 text-xs leading-snug text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
            {comments}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/**
 * Free-text cell that keeps keystrokes local and commits on blur so typing
 * doesn't rebuild the column array per keypress (LcResolutionBoard's TextCell).
 */
const TextCell: React.FC<{
  incoming: string;
  className: string;
  maxLength?: number;
  disabled?: boolean;
  onCommit: (value: string) => void;
}> = ({ incoming, className, maxLength, disabled, onCommit }) => {
  const [val, setVal] = useState(incoming);
  useEffect(() => { setVal(incoming); }, [incoming]);
  return (
    <input
      type="text"
      value={val}
      maxLength={maxLength}
      disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val !== incoming) onCommit(val); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={className}
    />
  );
};

function UnbilledChargesContent() {
  // Any orders-scope registry field can be added as a column on the REVIEW grid; the
  // Summary pivot is aggregated, so it has no pro to attach anything to.
  const fieldSelection = useAdditionalFieldSelection('unbilled-charges-review');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;
  const { showToast } = useToast();

  // Field-level edit gates. defaultDisabled: an un-granted user sees values
  // read-only; the two header chips (PermissionsContainer) are what an admin
  // configures in permissions mode.
  const canEditLcReason = useResolvedAccessLevel(LC_COMPONENT, false, true) >= 2;
  const canEditMgmt = useResolvedAccessLevel(MGMT_COMPONENT, false, true) >= 2;
  // The grantable-component chips only render while an admin has permissions
  // mode on (they're the click targets for granting) — users never see them.
  const inPermissionsMode = useTryPermissionsMode()?.permissionsMode ?? false;
  // Admin-published default layout for the review grid. Gating is TanTable's
  // own edit-default-table-settings PermissionsContainer (the LC Dashboards
  // pattern) — no second gate here; the backend re-checks the component on save.
  const [tableDefaults, setTableDefaults] = useState<any>(undefined);

  // Tabs
  const [tab, setTab] = useState<'review' | 'matrix'>('review');
  useFavoriteTab(tab, setTab, ['review', 'matrix']);

  // Data state
  const [data, setData] = useState<UnbilledChargeRow[]>([]);
  const [matrixData, setMatrixData] = useState<MatrixDatum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Date range (delivery date). A remembered preset re-resolves against
  // today on every visit; otherwise default to the last 30 days.
  const [activePreset, setActivePreset] = useState<DatePreset | null>(() => readStoredPreset());
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const preset = readStoredPreset();
    if (preset) return resolvePreset(preset).start;
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const preset = readStoredPreset();
    return preset ? resolvePreset(preset).end : new Date();
  });

  const applyDatePreset = useCallback((preset: DatePreset) => {
    const { start, end } = resolvePreset(preset);
    setStartDate(start);
    setEndDate(end);
    setActivePreset(preset);
    try { localStorage.setItem(PRESET_STORAGE_KEY, preset); } catch { /* private mode */ }
  }, []);

  // Delivered vs Charged window (remembered like the preset).
  const [dateBasis, setDateBasis] = useState<DateBasis>(() => readStoredBasis());
  const applyDateBasis = useCallback((basis: DateBasis) => {
    setDateBasis(basis);
    try { localStorage.setItem(BASIS_STORAGE_KEY, basis); } catch { /* private mode */ }
  }, []);

  // Manually touching a date picker exits preset mode.
  const clearPreset = useCallback(() => {
    setActivePreset(null);
    try { localStorage.removeItem(PRESET_STORAGE_KEY); } catch { /* private mode */ }
  }, []);

  // Filters. Categories behave like spreadsheet sheet-tabs: all rows are
  // fetched once and the active tab filters client-side (instant switch).
  const [activeCategory, setActiveCategory] = useState<string>('');
  // Hide rows already coded (Responsible Party set) — the default view is the
  // open queue; untick to see everything.
  const [hideCoded, setHideCoded] = useState(true);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);

  // Lookup options for the two dropdowns
  const [lcReasonOptions, setLcReasonOptions] = useState<string[]>([]);
  const [responsibleOptions, setResponsibleOptions] = useState<string[]>([]);

  // Summary controls: row axis, per-axis entity filter, Responsible filter,
  // and the responsible-breakdown toggle (sub-rows on/off).
  const [matrixAxis, setMatrixAxis] = useState<MatrixAxis>('region');
  const [matrixLcs, setMatrixLcs] = useState<string[]>([]);
  const [matrixDispatchers, setMatrixDispatchers] = useState<string[]>([]);
  const [matrixResponsible, setMatrixResponsible] = useState<string[]>([]);
  const [showRespBreakdown, setShowRespBreakdown] = useState(true);

  // LC filter options — individual LCs + standard/custom LC groups from the
  // shared LC-dashboard endpoint (LC Metrics' source; group values are
  // tilde-delimited member lists the backend expands). Fetched once, the
  // first time the LC axis is selected.
  const [lcFilterOptions, setLcFilterOptions] = useState<MultiSelectOption[]>([]);
  const lcOptionsRequested = useRef(false);
  useEffect(() => {
    if (matrixAxis !== 'lc' || lcOptionsRequested.current) return;
    lcOptionsRequested.current = true;
    api.get('/lc-dashboards/filter-options')
      .then((res: any) => {
        if (!isMounted.current) return;
        const all = (res?.data?.lcs || []) as Array<{ value: string; label: string; isGroup?: boolean }>;
        const groups = all.filter((o) => o.isGroup).map((o) => ({ value: o.value, label: o.label }));
        const individuals = all.filter((o) => !o.isGroup).map((o) => ({ value: o.value, label: o.label }));
        setLcFilterOptions(groups.length > 0
          ? [{ value: '__groups_header__', label: 'Groups', isHeader: true }, ...groups,
             { value: '__lcs_header__', label: 'LCs', isHeader: true }, ...individuals]
          : individuals);
      })
      .catch((error) => { console.error('Failed to fetch LC filter options:', error); });
  }, [matrixAxis]);

  // Dispatcher filter options — individual dispatchers + dispatcher groups
  // (same source as the dispatching pages' filters).
  const { options: dispatcherFilterOptions } = useOrderDispatcherOptions();

  // Responsible filter options: "(Not coded)" + the department taxonomy.
  // BILLED is excluded — the matrix always drops BILLED-resolved rows.
  const respFilterOptions = useMemo<MultiSelectOption[]>(() => [
    { value: NOT_CODED_LABEL, label: NOT_CODED_LABEL },
    ...responsibleOptions.filter((r) => r !== BILLED_OPTION).map((r) => ({ value: r, label: r })),
  ], [responsibleOptions]);

  // Staged edits keyed by `${pronumber}|${category}`
  const [edits, setEdits] = useState<RowEdits>({});
  const [saving, setSaving] = useState(false);
  const pendingCount = Object.keys(edits).length;

  // Order drawer
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  // Row key of the pinned rate-comments popover (one across the grid; other
  // rows suppress their hover previews while it's open).
  const [pinnedRateComments, setPinnedRateComments] = useState<string | null>(null);

  // Click-to-highlight: clicking a row toggles a yellow highlight on it
  // (click again to clear) so a reviewer can keep their place while working
  // down the list — Lisa's pick over blue. (The dark-bordered "unsaved edit"
  // cell mark is a different thing; it clears on Save/Discard.) Page state only
  // — a reload starts clean, by design. Clicks that land on the row's own
  // controls (dropdowns, inputs, links) don't toggle. Shared by the Review
  // grid and the drill-down (same row keys).
  const [markedRows, setMarkedRows] = useState<Set<string>>(() => new Set());
  const toggleMarked = useCallback((row: UnbilledChargeRow, event?: React.MouseEvent<HTMLElement>) => {
    const target = event?.target as HTMLElement | null;
    const rowEl = event?.currentTarget as HTMLElement | null;
    // A dropdown's option list is portaled to <body>, but React still bubbles
    // its clicks up the component tree to the row — so only a click that
    // physically lands inside the row counts, and not on one of its controls.
    if (rowEl && target && !rowEl.contains(target)) return;
    if (target?.closest?.('input, select, textarea, button, a, [role="combobox"], [role="listbox"], [role="option"], [contenteditable="true"]')) return;
    const key = rowKey(row);
    setMarkedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const markedRowClass = useCallback(
    (row: UnbilledChargeRow) => (markedRows.has(rowKey(row)) ? 'bg-yellow-100 dark:bg-yellow-900/40' : ''),
    [markedRows]
  );
  const markedCount = markedRows.size;
  const clearMarks = useCallback(() => setMarkedRows(new Set()), []);
  // Only shown while something is marked.
  const clearMarksButton = markedCount > 0 ? (
    <button
      type="button"
      onClick={clearMarks}
      title={`Remove the highlight from ${markedCount} row${markedCount === 1 ? '' : 's'}`}
      className="px-3 py-1 text-sm font-medium text-gray-700 bg-yellow-50 border border-yellow-300 rounded hover:bg-yellow-100 dark:text-gray-200 dark:bg-yellow-900/30 dark:border-yellow-700 dark:hover:bg-yellow-900/50"
    >
      Clear highlighting
    </button>
  ) : null;

  // Summary drill-down: the clicked cell and the rows behind it.
  const [drillCell, setDrillCell] = useState<MatrixCellRef | null>(null);
  const [drillRows, setDrillRows] = useState<UnbilledChargeRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);
  const drillAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  // Region + lookup options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const regionsRes = await ordersService.getRegions();
        if (!isMounted.current) return;
        setRegionOptions(
          regionsRes.data.regions.map((region: string) => ({ value: region, label: region }))
        );
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
      try {
        const defaults = await userSettingsService.getDefaultSetting('unbilled-charges-review');
        if (!isMounted.current) return;
        if (defaults) setTableDefaults(defaults);
      } catch (error) {
        console.error('Failed to fetch default table settings:', error);
      }
      try {
        const [reasons, parties] = await Promise.all([
          // `[{reason, requireComments}]` — the flag is for the Line Item Override
          // modal; this page only wants the names.
          api.get<{ reason: string; requireComments: boolean }[]>('/cms-lookups/unbilled-reasons'),
          // The V1 department taxonomy (same list as the cost lines'
          // groupresponsible) — coding matches Additional Cost's departments;
          // BILLED is prepended page-locally (see BILLED_OPTION).
          api.get<string[]>('/cms-lookups/departments'),
        ]);
        if (!isMounted.current) return;
        setLcReasonOptions(reasons.data.map((r) => r.reason));
        setResponsibleOptions([BILLED_OPTION, ...parties.data]);
      } catch (error) {
        console.error('Failed to fetch unbilled lookup options:', error);
      }
    };
    fetchOptions();
  }, []);

  const fetchData = useCallback(async (showLoading = false) => {
    // The saved selection is part of the request; going early just fetches twice.
    if (!fieldsReady) return;
    if (!isMounted.current) return;
    if (!startDate || !endDate) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setError(null);
      if (showLoading) setLoading(true);

      const params: Record<string, string> = {
        start_date: formatDateKey(startDate),
        end_date: formatDateKey(endDate),
        date_basis: dateBasis,
      };
      // `showLoading` marks the fetches a person caused — the first paint and every
      // filter change. The timer refresh and the reload after a save pass false, and
      // those are the page catching up rather than somebody asking for the report, so
      // they are not recorded as runs.
      if (!showLoading) params.background = 'true';
      if (tab === 'matrix') {
        params.rows = matrixAxis;
        if (matrixResponsible.length > 0) params.responsible = matrixResponsible.join(',');
        // Entity filters only apply on their own axis (the control is only
        // visible there — a hidden stale selection must not narrow the report).
        if (matrixAxis === 'lc' && matrixLcs.length > 0) params.lc = matrixLcs.join(',');
        if (matrixAxis === 'dispatcher' && matrixDispatchers.length > 0) params.dispatcher = matrixDispatchers.join(',');
        const response = await api.get('/unbilled-charges/matrix', {
          params,
          signal: controller.signal,
        });
        if (!isMounted.current || controller.signal.aborted) return;
        setMatrixData(response.data);
      } else {
        if (selectedRegions.length > 0) params.region = selectedRegions.join(',');
        if (additionalFieldsKey) params.additional_fields = additionalFieldsKey;
        const response = await api.get('/unbilled-charges', {
          params,
          signal: controller.signal,
        });
        if (!isMounted.current || controller.signal.aborted) return;
        setData(response.data);
      }
    } catch (err: any) {
      if (!isMounted.current || controller.signal.aborted) return;
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError(errorMessage(err));
    } finally {
      if (isMounted.current && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, dateBasis, selectedRegions, tab, matrixAxis, matrixResponsible, matrixLcs, matrixDispatchers, fieldsReady, additionalFieldsKey]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // The rows behind the clicked Summary cell: the same endpoint as the grid in
  // its drill-down mode — the cell's coordinates plus everything the pivot was
  // computed under (window, basis, axis, the Summary's filters) so the rows add
  // up to the number that was clicked.
  const fetchDrill = useCallback(async (background = false) => {
    if (!drillCell || !startDate || !endDate) return;
    drillAbortRef.current?.abort();
    const controller = new AbortController();
    drillAbortRef.current = controller;
    try {
      setDrillError(null);
      if (!background) setDrillLoading(true);
      const params: Record<string, string> = {
        start_date: formatDateKey(startDate),
        end_date: formatDateKey(endDate),
        date_basis: dateBasis,
        axis: matrixAxis,
        axis_value: drillCell.axisValue,
      };
      if (drillCell.subValue !== undefined) params.sub_value = drillCell.subValue;
      if (drillCell.category) params.category = drillCell.category;
      if (matrixResponsible.length > 0) params.responsible = matrixResponsible.join(',');
      if (matrixAxis === 'lc' && matrixLcs.length > 0) params.lc = matrixLcs.join(',');
      if (matrixAxis === 'dispatcher' && matrixDispatchers.length > 0) params.dispatcher = matrixDispatchers.join(',');
      if (additionalFieldsKey) params.additional_fields = additionalFieldsKey;
      if (background) params.background = 'true';
      const response = await api.get('/unbilled-charges', { params, signal: controller.signal });
      if (!isMounted.current || controller.signal.aborted) return;
      setDrillRows(response.data);
    } catch (err: any) {
      if (!isMounted.current || controller.signal.aborted) return;
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setDrillError(errorMessage(err));
    } finally {
      if (isMounted.current && !controller.signal.aborted) setDrillLoading(false);
    }
  }, [drillCell, startDate, endDate, dateBasis, matrixAxis, matrixResponsible, matrixLcs, matrixDispatchers, additionalFieldsKey]);

  useEffect(() => {
    if (drillCell) {
      setDrillRows([]);
      fetchDrill();
    }
  }, [drillCell, fetchDrill]);

  const closeDrill = useCallback(() => {
    drillAbortRef.current?.abort();
    setDrillCell(null);
    setDrillRows([]);
    setDrillError(null);
  }, []);

  // Background refresh (paused while edits are staged / saving / a drawer is
  // open, and while the tab is hidden) so coding done elsewhere — cost-line
  // departments, other users' saves, billing — flows in without a manual
  // reload. Same pattern as the TTs & Service Failures board.
  const autoRefreshPaused = pendingCount > 0 || saving || isOrderDrawerOpen;
  useVisibilityAwareInterval(
    () => { fetchData(false); },
    autoRefreshPaused ? null : 60_000,
  );

  // Latest rows, for comparing a new value against what's saved (refs so the
  // column defs, which close over setRowEdit, don't rebuild on every fetch).
  const dataRef = useRef(data);
  dataRef.current = data;
  const drillRowsRef = useRef(drillRows);
  drillRowsRef.current = drillRows;

  /** Stage `value` for one cell — or, when it's back to the saved value,
   *  un-stage it, so an undone change isn't marked or sent. */
  const stageValue = useCallback((prev: RowEdits, row: UnbilledChargeRow, col: EditableField, value: string): RowEdits => {
    const key = rowKey(row);
    if (value === (row[col] ?? '')) {
      if (prev[key]?.[col] === undefined) return prev;
      const { [col]: _dropped, ...rest } = prev[key];
      const next = { ...prev };
      if (Object.keys(rest).length > 0) next[key] = rest; else delete next[key];
      return next;
    }
    return { ...prev, [key]: { ...prev[key], [col]: value } };
  }, []);

  const setRowEdit = useCallback((key: string, col: EditableField, value: string) => {
    const row = dataRef.current.find((r) => rowKey(r) === key)
      ?? drillRowsRef.current.find((r) => rowKey(r) === key);
    if (!row) return;
    setEdits((prev) => stageValue(prev, row, col, value));
  }, [stageValue]);

  /** Staged value if present, else the stored value. */
  const effective = useCallback(
    (row: UnbilledChargeRow, col: EditableField): string => {
      const staged = edits[rowKey(row)]?.[col];
      if (staged !== undefined) return staged;
      return row[col] ?? '';
    },
    [edits]
  );

  const handleSave = useCallback(async () => {
    const keys = Object.keys(edits);
    if (keys.length === 0) return;
    setSaving(true);
    try {
      const items = keys.map((key) => {
        const [pronumber, category] = key.split('|');
        return { pronumber, category, ...edits[key] };
      });
      await api.patch('/unbilled-charges/reviews', { items });
      setEdits({});
      showToast({
        type: 'success',
        message: `Saved ${items.length} row${items.length === 1 ? '' : 's'}`,
      });
      fetchData();
      if (drillCell) fetchDrill(true);
    } catch (err) {
      // Whole batch is one transaction server-side — keep everything staged.
      showToast({ type: 'error', message: `Save failed: ${errorMessage(err)}` });
    } finally {
      setSaving(false);
    }
  }, [edits, fetchData, fetchDrill, drillCell, showToast]);

  const handleBulkEdit = useCallback(
    (columnId: string, selectedRows: UnbilledChargeRow[], value: unknown) => {
      const col = columnId as EditableField;
      if (col === 'lc_reason' && !canEditLcReason) return;
      if ((col === 'responsible_party' || col === 'mgr_comment') && !canEditMgmt) return;
      if (!['lc_reason', 'responsible_party', 'mgr_comment'].includes(col)) return;
      const v = (value as string) ?? '';
      setEdits((prev) => {
        let next = prev;
        for (const row of selectedRows) {
          // Cost-line departments own the Responsible Party for their rows.
          if (col === 'responsible_party' && row.groupresponsible) continue;
          // The invoice override owns the reason for its rows (the cell shows
          // it, not the dropdown — a staged value there would be invisible).
          if (col === 'lc_reason' && row.override_reason) continue;
          next = stageValue(next, row, col, v);
        }
        return next;
      });
    },
    [canEditLcReason, canEditMgmt, stageValue]
  );

  const handleSaveDefaults = useCallback(async (settingsKey: string, stored: any) => {
    await userSettingsService.saveDefaultSetting(settingsKey, stored);
    setTableDefaults(stored);
    showToast({ type: 'success', message: 'Default layout published for all users' });
  }, [showToast]);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Staged (unsaved) edits are marked on the CELL — an inset dark ring on the
  // <td>, via TanTable's getCellClassName — not on the control inside it (a
  // ring hugging the rounded dropdown was easy to miss) and not a fill (it
  // has to stay visible on a yellow-highlighted row). Lisa, 2026-09-24.
  const editedCellClassName = useCallback(
    (row: UnbilledChargeRow, columnId: string) =>
      edits[rowKey(row)]?.[columnId as EditableField] !== undefined
        ? 'ring-2 ring-inset ring-gray-900 dark:ring-gray-100'
        : '',
    [edits]
  );

  const baseColumns = useMemo<ColumnDef<UnbilledChargeRow>[]>(() => {
    const cellClass = () =>
      'w-full px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 rounded bg-transparent disabled:opacity-60 disabled:cursor-not-allowed';

    // Surface a stored-but-retired value as its own option so it still displays.
    const selectOptions = (opts: string[], current: string): SingleSelectOption[] => {
      const base = opts.map((r) => ({ value: r, label: r }));
      return current && !opts.includes(current) ? [{ value: current, label: current }, ...base] : base;
    };

    return [
      {
        accessorKey: 'pronumber',
        header: 'PRO #',
        filterFn: 'arrIncludes',
        footer: () => <span className="font-semibold">Totals</span>,
        cell: ({ row }) => {
          const pronumber = row.original.pronumber;
          return (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openOrderDrawer(Number(pronumber));
                }}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
              >
                {pronumber}
              </button>
              <a
                href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                title="Open in CMSV1"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            </div>
          );
        },
      },
      {
        accessorKey: 'category',
        header: 'Category',
        filterFn: 'arrIncludes',
        cell: (info) => ALL_CATEGORY_LABELS[info.getValue<string>()] || info.getValue<string>(),
      },
      {
        accessorKey: 'container',
        header: 'Container',
        filterFn: 'arrIncludes',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        accessorKey: 'delivery_date',
        header: 'Delivery Date',
        filterFn: 'arrIncludes',
        sortingFn: 'datetime',
        // Blank = no delivery appointment yet (a container still at the port
        // accruing charges) — such rows only ever appear on the Charged basis.
        cell: (info) => {
          const v = info.getValue<string>();
          return v
            ? formatIsoDateOnly(v)
            : <span className="italic text-muted" title="No delivery appointment yet">not delivered</span>;
        },
      },
      {
        // When the exposure was incurred: the first cost-line date, with the last
        // alongside when the row spans more than one day. The accessor stays the
        // first date (ISO) so sorting and the date facets keep working; only the
        // cell shows the span.
        accessorKey: 'charge_date',
        header: 'Charge Date',
        filterFn: 'arrIncludes',
        sortingFn: 'datetime',
        cell: ({ row }) => {
          const first = row.original.charge_date;
          const last = row.original.last_charge_date;
          if (!first) return <span className="text-gray-400 dark:text-gray-500">-</span>;
          if (last && last !== first) {
            return (
              <span title={`Cost lines dated ${formatIsoDateOnly(first)} through ${formatIsoDateOnly(last)}`}>
                {formatIsoDateOnly(first)}
                <span className="text-muted"> – {formatIsoDateOnly(last)}</span>
              </span>
            );
          }
          return formatIsoDateOnly(first);
        },
      },
      {
        accessorKey: 'billto',
        header: 'Bill To',
        filterFn: 'arrIncludes',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        accessorKey: 'customer',
        header: 'Customer',
        filterFn: 'arrIncludes',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        accessorKey: 'csr_name',
        header: 'CSR / LC',
        filterFn: 'arrIncludes',
        cell: ({ row }) => (
          <span title={row.original.csr}>{row.original.csr_name || row.original.csr || '-'}</span>
        ),
      },
      {
        accessorKey: 'dispatcher',
        header: 'Dispatcher',
        filterFn: 'arrIncludes',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        accessorKey: 'region',
        header: 'Region',
        filterFn: 'arrIncludes',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        accessorKey: 'cost',
        // Cost means the same on every tab (2026-10-30): what we paid for the move —
        // vendor cost, or net driver pay on the legs the charge fired on for Pre Pull
        // and Stop Off. The reconciler's missing charge is the next column.
        header: 'Cost',
        cell: ({ row }) => (
          <span className="tabular-nums">{formatCurrency(row.original.cost)}</span>
        ),
        footer: ({ table }) => (
          <span className="font-semibold tabular-nums">
            {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'cost'))}
          </span>
        ),
        meta: { exportValue: (row: any) => row.cost },
      },
      {
        // Invoice Reconciliation's missing charge — what the invoice should carry for
        // this row. Only the estimate-sourced tabs have one; a cost row shows a dash.
        accessorKey: 'suggested_charge',
        header: 'Suggested Charge',
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.suggested_charge == null ? '—' : formatCurrency(row.original.suggested_charge)}
          </span>
        ),
        footer: ({ table }) => (
          <span className="font-semibold tabular-nums">
            {formatCurrency(
              table.getFilteredRowModel().rows.reduce((acc, r) => acc + (r.original.suggested_charge || 0), 0)
            )}
          </span>
        ),
        meta: { exportValue: (row: any) => row.suggested_charge },
      },
      {
        accessorKey: 'billback',
        header: 'Bill Back',
        cell: ({ row }) => (
          <span className="tabular-nums text-muted">
            {row.original.billback ? formatCurrency(row.original.billback) : '—'}
          </span>
        ),
        footer: ({ table }) => (
          <span className="font-semibold tabular-nums">
            {formatCurrency(
              table.getFilteredRowModel().rows.reduce((acc, r) => acc + (r.original.billback || 0), 0)
            )}
          </span>
        ),
        meta: { exportValue: (row: any) => row.billback },
      },
      {
        accessorKey: 'revenue',
        header: 'Billed Rev',
        cell: ({ row }) => (
          <span className="tabular-nums">{formatCurrency(row.original.revenue)}</span>
        ),
        footer: ({ table }) => (
          <span className="font-semibold tabular-nums">
            {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'revenue'))}
          </span>
        ),
        meta: { exportValue: (row: any) => row.revenue },
      },
      {
        accessorKey: 'margin',
        header: 'Margin',
        cell: ({ row }) => (
          <span className="tabular-nums text-red-600 dark:text-red-400">
            {formatCurrency(row.original.margin)}
          </span>
        ),
        footer: ({ table }) => (
          <span className="font-semibold tabular-nums text-red-600 dark:text-red-400">
            {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'margin'))}
          </span>
        ),
        meta: { exportValue: (row: any) => row.margin },
      },
      {
        accessorKey: 'approvedby',
        header: 'Approved By',
        filterFn: 'arrIncludes',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        accessorKey: 'ratecomments',
        header: 'Rate Comments',
        enableColumnFilter: false,
        size: 300,
        // multiline opts out of TruncatedCell's wrapper (and its own tooltip) —
        // the cell truncates itself and owns the hover popover.
        meta: { exportValue: (row: any) => row.ratecomments, multiline: true },
        cell: ({ row }) => {
          if (!row.original.ratecomments) {
            return <span className="text-gray-400 dark:text-gray-500">-</span>;
          }
          const key = rowKey(row.original);
          return (
            <RateCommentsCell
              comments={row.original.ratecomments}
              pronumber={row.original.pronumber}
              pinned={pinnedRateComments === key}
              hoverSuppressed={pinnedRateComments !== null && pinnedRateComments !== key}
              onPinnedChange={(p) => setPinnedRateComments(p ? key : null)}
            />
          );
        },
      },
      {
        // Accessor = what the cell shows (override first), so the filter
        // dropdown, sorting and the export see the displayed reason.
        id: 'lc_reason',
        accessorFn: (r: UnbilledChargeRow) => r.override_reason || r.lc_reason,
        header: 'Override / LC Reason',
        filterFn: 'arrIncludes',
        size: 180,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: lcReasonOptions.map((r) => ({ value: r, label: r })),
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          // An invoice line-item override on this PRO's charge type is the
          // invoicing side's decision of record (its reason comes from the same
          // Override Reasons list), so it shows in place of the LC Reason —
          // read-only, with who/when; the LC's own coding, if any, is noted in
          // the tooltip. Override wins when both exist (Lisa, 2026-09-23).
          if (row.original.override_reason) {
            const r = row.original;
            const when = r.override_at ? ` on ${formatIsoDateOnly(r.override_at)}` : '';
            const lcNote = r.lc_reason ? `\nLC Reason underneath: ${r.lc_reason}` : '';
            // The LC's Additional Comments on the override — the reason is a category,
            // the comment is the case. Shown in its own column too; here so the whole
            // decision is readable from the chip without moving the eye.
            const note = r.override_comments ? `\nComments: ${r.override_comments}` : '';
            // The chip clips to the column, so the hover leads with the full
            // reason (newlines render as line breaks in a native tooltip).
            return (
              <span
                title={`${r.override_reason}${note}\nInvoice override — set by ${r.override_by || 'unknown'}${when}${lcNote}`}
                className="inline-flex items-center gap-1 max-w-full rounded px-1.5 py-0.5 text-xs bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-700"
              >
                <LockClosedIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="truncate">{r.override_reason}</span>
              </span>
            );
          }
          const key = rowKey(row.original);
          const v = effective(row.original, 'lc_reason');
          // Full value first (the select clips long reasons), then who set it.
          const setBy = row.original.lc_updated_by
            ? `Set by ${row.original.lc_updated_by}${row.original.lc_updated_at ? ` (${row.original.lc_updated_at.slice(0, 16).replace('T', ' ')})` : ''}`
            : '';
          const title = [v, setBy].filter(Boolean).join('\n') || undefined;
          return (
            <div title={title}>
              <SingleSelect
                options={selectOptions(lcReasonOptions, v)}
                value={v}
                onChange={(val) => setRowEdit(key, 'lc_reason', val)}
                disabled={!canEditLcReason}
                enableSearch
                searchPlaceholder="Search reasons…"
                size="sm"
                usePortal
                showClearOption
                clearOptionLabel="—"
                placeholder="—"
                ariaLabel="LC Reason"
                className="w-full"
              />
            </div>
          );
        },
      },
      {
        // The LC's Additional Comments on the invoice override. READ-ONLY and its own
        // column rather than folded into Comment: that one is the manager's own note
        // (`mgr_comment`, editable under unbilled-mgmt-fields), and writing a different
        // author's text into an editable field would read as something a manager typed
        // and could be overwritten by them. Empty for a row with no override, which is
        // most of them.
        accessorKey: 'override_comments',
        header: 'Override Comment',
        size: 200,
        cell: ({ row }) => {
          const v = row.original.override_comments;
          if (!v) return null;
          return (
            <span className="block truncate text-xs text-slate-700 dark:text-gray-300" title={v}>
              {v}
            </span>
          );
        },
        meta: {
          exportValue: (row: any) => row.override_comments,
          multiline: true,
        },
      },
      {
        // Accessor mirrors the cell: departments coded on the Additional Cost
        // lines (groupresponsible) win over the manual annotation, so the
        // filter dropdown and sorting see the value the cell displays.
        id: 'responsible_party',
        accessorFn: (r: UnbilledChargeRow) => r.groupresponsible || r.responsible_party,
        header: 'Responsible Party',
        filterFn: 'arrIncludes',
        size: 180,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: responsibleOptions.map((r) => ({ value: r, label: r })),
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          // Departments coded on the Additional Cost lines win — display them
          // read-only; the manual select is only for rows without them.
          if (row.original.groupresponsible) {
            return (
              <span title="From the Additional Cost lines' departments">
                {row.original.groupresponsible}
              </span>
            );
          }
          const key = rowKey(row.original);
          const v = effective(row.original, 'responsible_party');
          const title = row.original.mgmt_updated_by
            ? `Set by ${row.original.mgmt_updated_by}${row.original.mgmt_updated_at ? ` (${row.original.mgmt_updated_at.slice(0, 16).replace('T', ' ')})` : ''}`
            : undefined;
          return (
            <div title={title}>
              <SingleSelect
                options={selectOptions(responsibleOptions, v)}
                value={v}
                onChange={(val) => setRowEdit(key, 'responsible_party', val)}
                disabled={!canEditMgmt}
                enableSearch
                searchPlaceholder="Search departments…"
                size="sm"
                usePortal
                showClearOption
                clearOptionLabel="—"
                placeholder="—"
                ariaLabel="Responsible Party"
                className="w-full"
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'mgr_comment',
        header: 'Comment',
        filterFn: 'arrIncludes',
        size: 420,
        // multiline opts out of TruncatedCell's fixed-width wrapper so the
        // input stretches with the column.
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta, multiline: true },
        cell: ({ row }) => {
          const key = rowKey(row.original);
          return (
            <TextCell
              incoming={effective(row.original, 'mgr_comment')}
              maxLength={2000}
              disabled={!canEditMgmt}
              onCommit={(value) => setRowEdit(key, 'mgr_comment', value)}
              className={cellClass()}
            />
          );
        },
      },
    ];
  }, [edits, effective, setRowEdit, openOrderDrawer, canEditLcReason, canEditMgmt, lcReasonOptions, responsibleOptions, pinnedRateComments, activeCategory]);

  // Rows are PRO x category, so a pro with two unbilled categories is two rows and an
  // added order column repeats down them.
  const { columns, availableFields } = useAdditionalFieldColumns<UnbilledChargeRow>(
    fieldSelection, { baseColumns },
  );

  // Coded = the cost lines carry a department (groupresponsible) OR a manual
  // Responsible Party annotation exists.
  const isCoded = (r: UnbilledChargeRow) => !!(r.responsible_party || r.groupresponsible);

  // Sheet-tab filtering: rows for the active category tab; counts label the
  // tabs. The hide-coded toggle applies first so counts track the open queue.
  const baseRows = useMemo(
    () => (hideCoded ? data.filter((r) => !isCoded(r)) : data),
    [data, hideCoded]
  );
  const visibleRows = useMemo(
    () => (activeCategory ? baseRows.filter((r) => r.category === activeCategory) : baseRows),
    [baseRows, activeCategory]
  );
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of baseRows) counts[r.category] = (counts[r.category] ?? 0) + 1;
    return counts;
  }, [baseRows]);
  const codedCount = useMemo(
    () => data.reduce((n, r) => n + (isCoded(r) ? 1 : 0), 0),
    [data]
  );

  const getRowId = useCallback((row: UnbilledChargeRow) => rowKey(row), []);

  const tabButtonClass = (active: boolean) =>
    `px-3 py-1.5 text-sm font-medium rounded ${
      active
        ? 'bg-blue-600 text-white'
        : 'text-gray-600 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-700'
    }`;

  return (
    <div className="flex flex-col h-full">
      <FilterBar label="">
        <div className="flex items-center gap-1">
          <button type="button" className={tabButtonClass(tab === 'review')} onClick={() => setTab('review')}>
            Review
          </button>
          <button type="button" className={tabButtonClass(tab === 'matrix')} onClick={() => setTab('matrix')}>
            Summary
          </button>
        </div>
        <FilterBar.Divider />
        {tab === 'review' && (
          <>
            <MultiSelect
              options={regionOptions}
              value={selectedRegions}
              onChange={setSelectedRegions}
              placeholder="All Regions"
              showClearOption={true}
              clearOptionLabel="All Regions"
              size="sm"
            />
            <FilterBar.Divider />
          </>
        )}
        <div className="flex items-center gap-1">
          {(Object.keys(DATE_BASIS_LABELS) as DateBasis[]).map((basis) => (
            <button
              key={basis}
              type="button"
              onClick={() => applyDateBasis(basis)}
              title={basis === 'charge'
                ? 'Window on the cost lines’ own dates (driver-pay event date / additional-cost create date) — each row shows only the cost incurred in the range, so a charge that keeps accruing shows again each month with that month’s amount'
                : 'Window on the order’s delivery date — the row’s whole amount'}
              aria-pressed={dateBasis === basis}
              className={`px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${
                dateBasis === basis
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-slate-300 text-gray-600 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {DATE_BASIS_LABELS[basis]}
            </button>
          ))}
          <span className="ml-1">between</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={startDate}
            onChange={(val) => { setStartDate((val as Date | null) ?? null); clearPreset(); }}
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="Start"
            autoApply
          />
          <span>and</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={endDate}
            onChange={(val) => { setEndDate((val as Date | null) ?? null); clearPreset(); }}
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="End"
            autoApply
          />
          {([['today', 'Today'], ['mtd', 'MTD'], ['lastMonth', 'Last Month'], ['ytd', 'YTD']] as const).map(([preset, label]) => (
            <button
              key={preset}
              type="button"
              onClick={() => applyDatePreset(preset)}
              className={`px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${
                activePreset === preset
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-slate-300 text-gray-600 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <FilterBar.Divider />
        {tab === 'review' ? (
          <>
            {/* Grant targets for the two field-level components — only visible
                while an admin has permissions mode on. */}
            {inPermissionsMode && (
              <>
                <FilterBar.Divider />
                <PermissionsContainer
                  componentId={LC_COMPONENT}
                  componentName="Unbilled LC Reason"
                  defaultDisabled
                >
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300">
                    LC Reason: {canEditLcReason ? 'edit' : 'view'}
                  </span>
                </PermissionsContainer>
                <PermissionsContainer
                  componentId={MGMT_COMPONENT}
                  componentName="Unbilled Responsible Party & Comment"
                  defaultDisabled
                >
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300">
                    Mgmt fields: {canEditMgmt ? 'edit' : 'view'}
                  </span>
                </PermissionsContainer>
              </>
            )}
          </>
        ) : null}
      </FilterBar>

      {/* Summary controls get their own row — they don't fit next to the date
          range without wrapping mid-label. */}
      {tab === 'matrix' && (
        <div className="flex items-center flex-wrap gap-3 px-6 py-2 text-sm text-label bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <span className="whitespace-nowrap">Report by</span>
            {(Object.keys(MATRIX_AXIS_LABELS) as MatrixAxis[]).map((axis) => (
              <button
                key={axis}
                type="button"
                onClick={() => setMatrixAxis(axis)}
                className={`px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${
                  matrixAxis === axis
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-slate-300 text-gray-600 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {MATRIX_AXIS_LABELS[axis]}
              </button>
            ))}
          </div>
          <FilterBar.Divider />
          {matrixAxis === 'lc' && (
            <MultiSelect
              options={lcFilterOptions}
              value={matrixLcs}
              onChange={setMatrixLcs}
              placeholder="All LCs"
              showClearOption={true}
              clearOptionLabel="All LCs"
              enableSearch
              searchPlaceholder="Search LCs…"
              preserveOrder
              size="sm"
            />
          )}
          {matrixAxis === 'dispatcher' && (
            <MultiSelect
              options={dispatcherFilterOptions}
              value={matrixDispatchers}
              onChange={setMatrixDispatchers}
              placeholder="All Dispatchers"
              showClearOption={true}
              clearOptionLabel="All Dispatchers"
              enableSearch
              searchPlaceholder="Search dispatchers…"
              preserveOrder
              size="sm"
            />
          )}
          <MultiSelect
            options={respFilterOptions}
            value={matrixResponsible}
            onChange={setMatrixResponsible}
            placeholder="All Responsible"
            showClearOption={true}
            clearOptionLabel="All Responsible"
            preserveOrder
            size="sm"
          />
          <FilterBar.Divider />
          <div
            className="flex items-center gap-2 whitespace-nowrap"
            title="Show the responsible-party split under each row"
          >
            <Toggle
              checked={showRespBreakdown}
              onChange={setShowRespBreakdown}
              size="sm"
              ariaLabel="Show responsible breakdown"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300 select-none">
              Resp breakdown
            </span>
          </div>
        </div>
      )}

      <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        {tab === 'review'
          ? `Accessorial charges we paid for but have not (fully) billed — cost exceeds billed revenue. ${
              dateBasis === 'charge'
                ? 'Windowed on when the charges were incurred, including containers not delivered yet: each row shows only the cost dated in the range (billed revenue and margin in the same proportion), so a charge that keeps accruing shows again each month with that month’s amount.'
                : 'Windowed on the delivery date.'
            } Rows disappear as soon as the charge is invoiced.`
          : `Unbilled charges by ${MATRIX_AXIS_LABELS[matrixAxis]} and category${
              dateBasis === 'charge' ? ' — only the cost incurred in the range, revenue in proportion' : ''
            }.${
              showRespBreakdown
                ? ` Click a row to expand its responsible-party split; "${NOT_CODED_LABEL}" = rows without a Responsible Party yet.`
                : ''
            } Click an amount to see the charges behind it. Rows resolved as BILLED are excluded.`}
      </div>

      {error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 p-4">
          {tab === 'review' ? (
            <div className="flex flex-col h-full min-h-0">
              {/* Category sheet-tabs (mimics the workbook's per-category sheets). */}
              <div className="flex items-end gap-0.5 overflow-x-auto pb-0 mb-2">
                {[['', `All (${baseRows.length})`] as [string, string]]
                  .concat(Object.entries(ALL_CATEGORY_LABELS).map(([key, label]) =>
                    [key, `${label} (${categoryCounts[key] ?? 0})`] as [string, string]))
                  .map(([key, label]) => {
                    const active = activeCategory === key;
                    return (
                      <button
                        key={key || 'all'}
                        type="button"
                        onClick={() => setActiveCategory(key)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-t border whitespace-nowrap ${
                          active
                            ? 'bg-panel text-blue-700 dark:text-blue-300 border-gray-300 dark:border-gray-600 border-b-white dark:border-b-gray-800 -mb-px font-semibold'
                            : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                <div
                  className="ml-3 mb-1 flex items-center gap-2 whitespace-nowrap"
                  title="Hide rows that already have a Responsible Party"
                >
                  <Toggle
                    checked={hideCoded}
                    onChange={setHideCoded}
                    size="sm"
                    ariaLabel="Hide coded rows"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-300 select-none">
                    Hide coded ({codedCount})
                  </span>
                </div>
                <div className="ml-auto mb-1 flex items-center gap-2">
                  {clearMarksButton}
                  {pendingCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setEdits({})}
                      disabled={saving}
                      className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                      Discard
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={pendingCount === 0 || saving}
                    className="px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : pendingCount > 0 ? `Save ${pendingCount} row${pendingCount === 1 ? '' : 's'}` : 'Save'}
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0">
            <TanTable
              data={visibleRows}
              columns={columns}
              availableFields={availableFields}
              additionalFields={fieldSelection.additionalFields}
              onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
              trackViewedColumnId="pronumber"
              loading={loading}
              defaultSorting={[{ id: 'delivery_date', desc: true }]}
              settingsKey="unbilled-charges-review"
              enableColumnFilters={true}
              enableGrouping={true}
              getRowId={getRowId}
              cardTitle={activeCategory ? `${ALL_CATEGORY_LABELS[activeCategory]} — Unbilled` : 'Unbilled Charges'}
              onRowClick={toggleMarked}
              getRowClassName={markedRowClass}
              getCellClassName={editedCellClassName}
              fillContainer={true}
              onBulkEdit={handleBulkEdit}
              showColumnFooter
              defaultSettings={tableDefaults}
              canEditDefaults
              onSaveDefaults={handleSaveDefaults}
            />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {/* With the breakdown hidden the responsible parties aren't visible
                  in the table, so an active Responsible filter must announce
                  itself — the totals are partial. */}
              {matrixResponsible.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap text-xs text-amber-800 dark:text-amber-200">
                  <span className="font-medium">Responsible filter:</span>
                  {matrixResponsible.map((v) => (
                    <span
                      key={v}
                      className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700"
                    >
                      {v}
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => setMatrixResponsible([])}
                    className="ml-1 underline text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
                  >
                    Clear
                  </button>
                </div>
              )}
              <MatrixTable
                data={matrixData}
                loading={loading}
                // Cost categories only: the pivot is accrued cost vs billed revenue, and
                // the estimate tabs have no cost side to pivot.
                // Both halves of the pivot: the cost categories and the estimate ones.
                // They were cost-only until 2026-10-13, which left Pre Pull and Stop Off
                // — the tab's two biggest counts — out of every Summary total.
                categories={Object.entries(ALL_CATEGORY_LABELS).map(([key, label]) => ({ key, label }))}
                axisHeader={
                  showRespBreakdown
                    ? `${MATRIX_AXIS_LABELS[matrixAxis]} / Responsible`
                    : MATRIX_AXIS_LABELS[matrixAxis]
                }
                blankAxisLabel={`(no ${matrixAxis === 'lc' ? 'LC' : MATRIX_AXIS_LABELS[matrixAxis].toLowerCase()})`}
                blankSubLabel={NOT_CODED_LABEL}
                showSubRows={showRespBreakdown}
                onCellClick={setDrillCell}
              />
            </div>
          )}
        </div>
      )}

      {/* The drawer moves into the drill-down while it's open so it paints on
          top of the modal (see DrilldownModal). */}
      {drillCell ? (
        <DrilldownModal
          cell={drillCell}
          context={`${DATE_BASIS_LABELS[dateBasis]} ${startDate ? formatIsoDateOnly(formatDateKey(startDate)) : '…'} – ${endDate ? formatIsoDateOnly(formatDateKey(endDate)) : '…'}${
            matrixResponsible.length > 0 ? ` · Responsible: ${matrixResponsible.join(', ')}` : ''
          }`}
          rows={drillRows}
          loading={drillLoading}
          error={drillError}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          getRowId={getRowId}
          onBulkEdit={handleBulkEdit}
          onRowClick={toggleMarked}
          getRowClassName={markedRowClass}
          getCellClassName={editedCellClassName}
          clearMarksButton={clearMarksButton}
          pendingCount={pendingCount}
          saving={saving}
          onSave={handleSave}
          onDiscard={() => setEdits({})}
          selectedPro={selectedPro}
          isProDrawerOpen={isOrderDrawerOpen}
          onCloseProDrawer={closeOrderDrawer}
          onClose={closeDrill}
        />
      ) : (
        <ProDetailSlideOver
          pronumber={selectedPro}
          isOpen={isOrderDrawerOpen && selectedPro !== null}
          onClose={closeOrderDrawer}
        />
      )}
    </div>
  );
}

function UnbilledCharges() {
  return (
    <ViewedProsProvider>
      <UnbilledChargesContent />
    </ViewedProsProvider>
  );
}

export default UnbilledCharges;
